import { ClearComponent } from './ClearComponent';
import { CheckboxActive, CheckboxDefault } from '@/assets/svg';
import CustomCountryCodeInput from '@/new-components/CustomCountryCodeInput';
import CustomDate from '@/new-components/CustomDate';
import CustomLoadingButton from '@/new-components/CustomLoadingButton';
import CustomSelectContainer from '@/new-components/CustomSelectOld/CustomSelectContainer';
import {
  Box,
  TextField,
  Typography,
  InputLabel,
  withStyles,
  InputAdornment,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { Divider } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import NumberFormat from 'react-number-format';

const UNIT_NUMBER_VALID_REGEX = /^[0-9]{1,2}-[0-9]{1,4}$/;

export const IDENTITY_TYPE = [
  {
    key: 'NRIC',
    value: 'NRIC/FIN',
  },
  {
    key: 'Passport',
    value: 'Passport',
  },
];

const CustomTitle = withStyles({
  root: {
    fontSize: 16,
    fontWeight: 600,

    color: '#000000',
  },
})(Typography);

const CustomInputLabel = withStyles({
  root: {
    color: '#666666',
    marginBottom: 8,

    fontWeight: 600,
  },
})(InputLabel);

const CustomTextField = withStyles({
  root: {
    fontSize: 20,

    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      height: 48,
    },
    '& .MuiOutlinedInput-input': {
      marginRight: 0,
      paddingLeft: 5,
      fontSize: 16,
    },

    '& .MuiInputBase-inputMultiline': {
      '&.MuiInputBase-input': {
        marginLeft: 0,
      },
    },

    '& .Mui-disabled': {
      '&.MuiOutlinedInput-root': {
        backgroundColor: '#f6f6f6',
      },
    },
    '& .MuiInputBase-adornedStart': {
      '& .MuiTypography-root': {
        fontSize: 16,
        color: '#333333',
      },
      '& .MuiInputAdornment-positionStart': {
        marginRight: 0,
      },
      '& input': {
        marginLeft: 0,
      },
    },
    '& .MuiFormHelperText-root': {
      alignItems: 'center',
      display: 'flex',
      fontFamily: 'OpenSans',
      fontSize: 12,
      gap: 6,
      marginLeft: 2,

      '& .MuiSvgIcon-root': {
        fontSize: 'unset',
      },
    },
  },
})(TextField);

const CustomToggleButtonGroup = withStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
  },
  grouped: {
    margin: theme.spacing(0.5),
    border: 'none',
    '&:not(:first-child)': {
      borderLeft: '1px solid #E1E1E1',
      borderRadius: 8,
    },
    '&:first-child': {
      borderRadius: 8,
    },
  },
}))(ToggleButtonGroup);

const CustomToggleButton = withStyles((theme) => ({
  root: {
    flex: 1,
    border: '1px solid #E1E1E1',
    color: '#3333333',
    textTransform: 'unset',
    fontSize: 16,

    '&.Mui-selected': {
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.primary.light,

      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
    '&.error': {
      border: `1px solid ${theme.palette.feedback.error}`,
    },
  },
}))(ToggleButton);

const CompleteProfile = ({
  loading,
  formValue,
  errors,
  handleSubmit,
  handleChangeForm,
  unitNumber,
  handleChangeUnitNumber,
  handleClearForm,
  isSingpass,
  handleCountryCode,
  isSocialLogin,
}) => {
  const sanitizeUnitNumberDisplay = (raw) => {
    const digitsAndDash = (raw || '').replace(/[^0-9-]/g, '');
    const dashIndex = digitsAndDash.indexOf('-');
    const hasDash = dashIndex >= 0;

    const leftRaw = hasDash ? digitsAndDash.slice(0, dashIndex) : digitsAndDash;
    const rightRaw = hasDash
      ? digitsAndDash.slice(dashIndex + 1).replace(/-/g, '')
      : '';

    const left = leftRaw.slice(0, 2);
    const right = rightRaw.slice(0, 4);

    if (!hasDash) return left;
    return `${left}-${right}`;
  };

  const handleChangeUnitNumberLocal = (e) => {
    const value = sanitizeUnitNumberDisplay(e.target.value);
    handleChangeUnitNumber({ ...e, target: { ...e.target, value } });
  };

  const isEmptyParams = useMemo(() => {
    let filterKeys = [
      'fullName',
      'dateOfBirth',
      'gender',
      'identityType',
      'identityNumber',
      'postalCode',
      'address',
    ];

    let valid = false;
    // Unit Number (optional)
    if (
      !(
        (!isEmpty(formValue.floor) && !isEmpty(formValue.unitNumber)) ||
        (isEmpty(formValue.floor) && isEmpty(formValue.unitNumber))
      ) ||
      (!isEmpty(formValue.floor) &&
        !isEmpty(formValue.unitNumber) &&
        !UNIT_NUMBER_VALID_REGEX.test(unitNumber))
    ) {
      // Must be enter floor and unit number together and
      // If enter both floor and number, musst be follow format #[Floor(2 characters)]-[Unit]
      valid = true;
    }

    if (formValue.postalCode?.length < 6) valid = true;

    let checkValid = filterKeys.some((key) => !formValue[key]);
    if (isSingpass) {
      return checkValid || valid || !formValue.email || !formValue.phoneNumber;
    } else {
      return checkValid || valid;
    }

    // return checkValid || valid;
  }, [formValue, isSingpass, unitNumber]);

  const handleInputUnitNumber = (e) => {
    const inputType = e?.nativeEvent?.inputType || '';
    if (inputType.startsWith('delete')) return;

    const raw = e.target.value || '';
    const digitsOnly = raw.replace(/[^0-9]/g, '');
    if (!raw.includes('-') && digitsOnly.length === 2) {
      e.target.value = `${digitsOnly}-`;
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      mt={5}
      alignItems="center"
      overflow="hidden"
    >
      <Box
        maxWidth={600}
        width="100%"
        mb={2.5}
        className="container-scroll"
        height="calc(100vh - 200px)"
      >
        {isSocialLogin && (
          <>
            <CustomInputLabel>Email</CustomInputLabel>
            <CustomTextField
              fullWidth
              variant="outlined"
              value={formValue.email || ''}
              disabled
            />
            <CustomInputLabel style={{ marginTop: 24 }}>
              Phone number
            </CustomInputLabel>
            <Box display="flex" gridGap={8}>
              <CustomCountryCodeInput
                country={formValue.countryCode}
                disabled={false}
                onChange={handleCountryCode}
                isMobileSize
              />
              <NumberFormat
                id="phoneNumber"
                fullWidth
                customInput={CustomTextField}
                variant="outlined"
                placeholder="1234 5678"
                onValueChange={handleChangeForm('phoneNumber')}
                allowNegative={false}
                value={formValue.phoneNumber || ''}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                InputProps={{
                  endAdornment: (
                    <ClearComponent
                      id="phoneNumber"
                      value={formValue.phoneNumber}
                      onClear={() => handleClearForm('phoneNumber')}
                    />
                  ),
                }}
              />
            </Box>
            <Divider style={{ marginTop: 24, marginBottom: 0 }} />
          </>
        )}
        <CustomTitle style={{ marginTop: isSocialLogin ? 16 : 0 }}>
          Personal details
        </CustomTitle>
        <CustomInputLabel style={{ marginTop: 24 }}>Full name</CustomInputLabel>
        <CustomTextField
          disabled={isSingpass}
          id="fullName"
          fullWidth
          variant="outlined"
          placeholder="Enter full name as per NRIC"
          value={formValue.fullName || ''}
          onChange={handleChangeForm('fullName')}
          error={!!errors.fullName}
          helperText={errors.fullName}
          InputProps={{
            endAdornment: isSingpass ? null : (
              <ClearComponent
                id="fullName"
                value={formValue.fullName}
                onClear={() => handleClearForm('fullName')}
              />
            ),
          }}
        />
        <CustomInputLabel style={{ marginTop: 24 }}>
          Date of birth
        </CustomInputLabel>
        <CustomDate
          disabled={isSingpass}
          selectedDate={formValue.dateOfBirth}
          isFullWidth
          placeholder="DD/MM/YYYY"
          clearable={false}
          onChange={handleChangeForm('dateOfBirth')}
          maxDate={moment()}
          style={{ height: 48, fontFamily: 'OpenSans', fontSize: 16 }}
        />
        <CustomInputLabel style={{ marginTop: 24 }}>Gender</CustomInputLabel>
        <CustomToggleButtonGroup
          exclusive
          disabled={isSingpass}
          value={formValue?.gender}
          onChange={handleChangeForm('gender')}
        >
          <CustomToggleButton
            value="Female"
            className={errors.gender ? 'error' : ''}
            disabled={isSingpass}
          >
            Female
          </CustomToggleButton>
          <CustomToggleButton
            value="Male"
            className={errors.gender ? 'error' : ''}
            disabled={isSingpass}
          >
            Male
          </CustomToggleButton>
        </CustomToggleButtonGroup>
        <CustomInputLabel style={{ marginTop: 24 }}>ID type</CustomInputLabel>
        <CustomSelectContainer
          // disabled={isSingpass}
          data={IDENTITY_TYPE || []}
          height="48px"
          fontSize="16px"
          isMutiple={false}
          hideClear
          colorInput="#000000de"
          placeholder="Select"
          renderValue={
            formValue.identityType
              ? IDENTITY_TYPE.find((it) => it.key === formValue.identityType)
                  ?.value
              : 'Select'
          }
          filterValue={formValue.identityType || ''}
          onChange={handleChangeForm('identityType')}
        />

        <CustomInputLabel style={{ marginTop: 24 }}>NRIC</CustomInputLabel>
        <CustomTextField
          disabled={isSingpass}
          id="identityNumber"
          fullWidth
          variant="outlined"
          placeholder="Enter number"
          value={formValue.identityNumber || ''}
          onChange={handleChangeForm('identityNumber')}
          error={!!errors.identityNumber}
          helperText={errors.identityNumber}
          InputProps={{
            endAdornment: isSingpass ? null : (
              <ClearComponent
                id="identityNumber"
                value={formValue.identityNumber}
                onClear={() => handleClearForm('identityNumber')}
              />
            ),
          }}
        />
        {isSingpass && (
          <>
            <CustomInputLabel style={{ marginTop: 24 }}>Email</CustomInputLabel>
            <CustomTextField
              id="email"
              fullWidth
              variant="outlined"
              placeholder="email@address.com"
              autoComplete="email"
              value={formValue.email || ''}
              onChange={handleChangeForm('email')}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                endAdornment: (
                  <ClearComponent
                    id="email"
                    value={formValue.email}
                    onClear={() => handleClearForm('email')}
                  />
                ),
              }}
            />
            <CustomInputLabel style={{ marginTop: 24 }}>
              Phone number
            </CustomInputLabel>
            <Box display="flex" gridGap={8}>
              <CustomCountryCodeInput
                country={formValue.countryCode}
                onChange={handleCountryCode}
                isMobileSize
              />
              <NumberFormat
                id="phoneNumber"
                fullWidth
                customInput={CustomTextField}
                variant="outlined"
                placeholder="1234 5678"
                onValueChange={handleChangeForm('phoneNumber')}
                allowNegative={false}
                value={formValue.phoneNumber || ''}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                InputProps={{
                  endAdornment: (
                    <ClearComponent
                      id="phoneNumber"
                      value={formValue.phoneNumber}
                      onClear={() => handleClearForm('phoneNumber')}
                    />
                  ),
                }}
              />
            </Box>
          </>
        )}

        <Box my={2}>
          <Divider light />
        </Box>
        <CustomTitle>Address details</CustomTitle>
        <CustomInputLabel style={{ marginTop: 24 }}>Address</CustomInputLabel>
        <CustomTextField
          id="address"
          fullWidth
          variant="outlined"
          placeholder="Enter full address"
          value={formValue.address || ''}
          onChange={handleChangeForm('address')}
          error={!!errors.address}
          helperText={errors.address}
          InputProps={{
            endAdornment: (
              <ClearComponent
                id="address"
                value={formValue.address}
                onClear={() => handleClearForm('address')}
              />
            ),
          }}
        />
        <CustomInputLabel style={{ marginTop: 24 }}>
          Unit number
        </CustomInputLabel>
        <Typography variant="body2" style={{ color: '#666', marginBottom: 4 }}>
          Please key 00-00 if not applicable
        </Typography>
        <CustomTextField
          id="unitNumber"
          fullWidth
          variant="outlined"
          placeholder="12-34"
          value={unitNumber || ''}
          onChange={handleChangeUnitNumberLocal}
          onInput={handleInputUnitNumber}
          error={!!errors.unitNumber}
          helperText={errors.unitNumber}
          // disabled={formValue?.ignoredUnitNumber}
          InputProps={{
            startAdornment: <InputAdornment position="start">#</InputAdornment>,
            endAdornment: (
              <ClearComponent
                id="unitNumber"
                value={unitNumber}
                onClear={() => handleClearForm('unitNumber')}
              />
            ),
          }}
        />

        {/* <FormControlLabel
          control={
            <Checkbox
              style={{ marginLeft: 3 }}
              checked={formValue?.ignoredUnitNumber}
              icon={<CheckboxDefault />}
              checkedIcon={<CheckboxActive />}
              color="primary"
              onChange={handleChangeForm('ignoredUnitNumber')}
            />
          }
          label="I don't have a unit number"
        /> */}
        <CustomInputLabel style={{ marginTop: 24 }}>
          Postal code
        </CustomInputLabel>
        <CustomTextField
          id="postalCode"
          fullWidth
          variant="outlined"
          placeholder="Enter code"
          value={formValue.postalCode || ''}
          onChange={handleChangeForm('postalCode')}
          error={!!errors.postalCode}
          helperText={errors.postalCode}
          style={{ marginBottom: 24 }}
          onInput={(e) => {
            e.target.value = e.target.value.toString().slice(0, 6);
          }}
          InputProps={{
            endAdornment: (
              <ClearComponent
                id="postalCode"
                value={formValue.postalCode}
                onClear={() => handleClearForm('postalCode')}
              />
            ),
          }}
        />
      </Box>
      <Box width={'100%'}>
        <CustomLoadingButton
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          fullWidth
          loading={loading}
          style={{ width: '100%' }}
          disabled={isEmptyParams || loading}
        >
          {isSingpass ? 'Sign up' : 'Next'}
        </CustomLoadingButton>
      </Box>
    </Box>
  );
};

export default CompleteProfile;
